// 333secret.com cryptoquip state. Guesses are checked by the server; only cipher→letter pairs are kept here.
import { useCallback, useEffect, useRef, useState } from "react";
import { pub } from "@/lib/api";

const STORE = "quip_progress";
export const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const isLetter = (c) => LETTERS.includes(c);

const load = (version) => {
  try {
    const s = JSON.parse(localStorage.getItem(STORE) || "null");
    return s && s.version === version ? s : null;
  } catch { return null; }
};
const save = (s) => { try { localStorage.setItem(STORE, JSON.stringify(s)); } catch { /* private mode */ } };
export const forget = () => { try { localStorage.removeItem(STORE); } catch { /* private mode */ } };

let actx = null;
export function chirp(bad) {
  try {
    actx = actx || new (window.AudioContext || window.webkitAudioContext)();
    const t0 = actx.currentTime;
    [0, 1, 2].forEach((i) => {
      const o = actx.createOscillator(), g = actx.createGain(), t = t0 + i * 0.06;
      o.type = bad ? "sawtooth" : "square";
      o.frequency.setValueAtTime(bad ? 180 - i * 30 : 1500 + i * 700, t);
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(bad ? 0.05 : 0.035, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
      o.connect(g); g.connect(actx.destination); o.start(t); o.stop(t + 0.08);
    });
  } catch { /* no audio */ }
}

const pairsOf = (list) => Object.fromEntries(list.map((r) => [r.cipher, r.plain]));

export function useQuip() {
  const [puz, setPuz] = useState(null);
  const [st, setSt] = useState({ pairs: {}, strikes: 0, wipes: 0, total: 0 });
  const [bonus, setBonus] = useState([]);
  const [error, setError] = useState(false);
  const busy = useRef(false);
  const stRef = useRef(st);
  stRef.current = st;

  const fetchBonus = useCallback(async (p, n) => {
    if (!n) return [];
    const r = await pub.get("/cryptoquip/bonus", { params: { version: p.version, n } });
    return r.data.bonus;
  }, []);

  const init = useCallback(async () => {
    try {
      const p = (await pub.get("/cryptoquip")).data;
      const saved = load(p.version);
      const s = saved ? { pairs: saved.pairs || {}, strikes: saved.strikes || 0, wipes: saved.wipes || 0, total: saved.total || 0 }
        : { pairs: {}, strikes: 0, wipes: 0, total: 0 };
      if (!saved) forget();
      if (s.strikes >= 3) {  // reloaded mid-wipe: finish it
        Object.assign(s, { pairs: {}, strikes: 0, wipes: Math.min(s.wipes + 1, p.max_bonus) });
        save({ version: p.version, ...s });
        pub.post("/beacon", { event: "quip_wipe" }).catch(() => {});
      }
      setBonus(await fetchBonus(p, s.wipes));
      setSt(s);
      setPuz(p);
    } catch { setError(true); }
  }, [fetchBonus]);
  useEffect(() => { init(); }, [init]);

  const known = puz ? { ...pairsOf([puz.clue, ...bonus]), ...st.pairs } : {};
  const letters = puz ? [...new Set(Array.from(puz.cipher).filter(isLetter))] : [];
  const solved = !!puz && letters.every((c) => known[c]);
  const persist = (s) => { setSt(s); if (puz) save({ version: puz.version, ...s }); };

  // Returns "right" | "wrong" | "trace" (3rd strike) | null (ignored).
  const guess = async (cipher, plain) => {
    if (!puz || busy.current || solved || !cipher || known[cipher]) return null;
    busy.current = true;
    try {
      const r = await pub.post("/cryptoquip/guess", { version: puz.version, cipher, plain });
      if (r.data.correct) { persist({ ...st, pairs: { ...st.pairs, [cipher]: plain } }); return "right"; }
      const s = { ...st, strikes: st.strikes + 1, total: st.total + 1 };
      persist(s);
      return s.strikes >= 3 ? "trace" : "wrong";
    } catch (e) {
      if (e.response?.status === 409) { forget(); init(); }
      return null;
    } finally { busy.current = false; }
  };

  // TRACE COMPLETE: every solved letter goes, strikes reset, one more bonus letter (max 4) for next time.
  const wipe = async () => {
    const cur = stRef.current;
    const wipes = Math.min(cur.wipes + 1, puz.max_bonus);
    setBonus(await fetchBonus(puz, wipes).catch(() => bonus));
    persist({ pairs: {}, strikes: 0, wipes, total: cur.total });
    pub.post("/beacon", { event: "quip_wipe" }).catch(() => {});
  };

  const finish = () => {
    pub.post("/cryptoquip/solve", { version: puz.version, pairs: known, strikes: st.total }).catch(() => {});
    forget();  // a reload starts fresh
  };

  return { puz, error, known, letters, solved, strikes: st.strikes, guess, wipe, finish, isLetter };
}
